import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@material-ui/core'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import CancelIcon from '@material-ui/icons/Cancel'
import CloseIcon from '@material-ui/icons/Close'
import { FASTQ_NAMING_RULES } from '../../../utils/fastqNaming'

// "How should I name my FASTQ files?" - the rules the server actually applies
// when it pairs R1 with R2 and derives the sample name, written out so a user
// can fix a name before uploading tens of gigabytes.
const FastqNamingInfo = ({ size, variant }) => {
  const [open, setOpen] = useState(false)

  const Mono = ({ children }) => (
    <Box
      component="span"
      sx={{
        fontFamily: 'monospace',
        fontSize: '0.8125rem',
        px: 0.75,
        py: 0.25,
        borderRadius: 0.75,
        bgcolor: 'action.hover',
        wordBreak: 'break-all',
      }}
    >
      {children}
    </Box>
  )

  return (
    <>
      {variant === 'button' ? (
        <Button
          variant="outlined"
          size="small"
          startIcon={<HelpOutlineIcon />}
          onClick={(e) => {
            e.stopPropagation()
            setOpen(true)
          }}
          aria-label="FASTQ naming rules"
          sx={{
            fontSize: '0.75rem',
            py: 0.75,
            px: 2,
            borderRadius: 20,
            minWidth: '90px',
            height: '32px',
            textTransform: 'none',
          }}
        >
          Help
        </Button>
      ) : (
        <Tooltip title="FASTQ naming rules" arrow>
          <IconButton
            size={size}
            onClick={(e) => {
              e.stopPropagation()
              setOpen(true)
            }}
            aria-label="FASTQ naming rules"
          >
            <HelpOutlineIcon sx={{ fontSize: size === 'small' ? 18 : 22 }} />
          </IconButton>
        </Tooltip>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <span>How to name FASTQ files</span>
          <IconButton size="small" onClick={() => setOpen(false)} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            The two files of a pair are matched by their names: everything except the read indicator has to be
            identical. The part that is left over also becomes the sample name shown throughout the analysis.
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Accepted extensions
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
            {FASTQ_NAMING_RULES.accepted.map((ext) => (
              <Chip key={ext} label={ext} size="small" variant="outlined" />
            ))}
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Read indicator (must be last, just before the extension)
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
            {FASTQ_NAMING_RULES.readForms.map((form) => (
              <Chip key={form} label={form} size="small" variant="outlined" />
            ))}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Examples
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
            {FASTQ_NAMING_RULES.examples.map((ex) => (
              <Box key={ex.name} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                {ex.ok ? (
                  <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main', mt: 0.25, flexShrink: 0 }} />
                ) : (
                  <CancelIcon sx={{ fontSize: 18, color: 'error.main', mt: 0.25, flexShrink: 0 }} />
                )}
                <Box sx={{ minWidth: 0 }}>
                  <Mono>{ex.name}</Mono>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}>
                    {ex.ok ? `sample name: ${ex.label}` : ex.label}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Worth knowing
          </Typography>
          <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
            {FASTQ_NAMING_RULES.notes.map((note) => (
              <Typography component="li" variant="body2" key={note} sx={{ mb: 0.5, color: 'text.secondary' }}>
                {note}
              </Typography>
            ))}
          </Box>
        </DialogContent>

        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </Box>
      </Dialog>
    </>
  )
}

FastqNamingInfo.propTypes = {
  size: PropTypes.oneOf(['small', 'medium']),
  // 'icon' is the inline question mark; 'button' is the labelled Help button
  // that sits with the other actions in the Selected Files header.
  variant: PropTypes.oneOf(['icon', 'button']),
}

FastqNamingInfo.defaultProps = {
  size: 'small',
  variant: 'icon',
}

export default FastqNamingInfo
